/*
  Constructors that turn the page's in-memory state into the JSON payloads
  the admin_manager_server endpoints expect, plus the inverse helper that
  turns the validation API response into a flat valid/invalid breakdown
  with overrides applied.
*/
import { isValidArray, isValidArrayWithValues } from '../../utils/utilities';
import { isCancelledTicket, isTicketEligible } from './grouping';

export const buildValidationPayloadTrips = (trips) => trips.map((trip) => ({
    tripKey: trip.tripKey,
    journeyDate: trip.journeyDate,
    route: trip.route,
    serviceNumber: trip.serviceNumber,
    coach: trip.coach,
    tickets: trip.tickets.filter(isTicketEligible).map((ticket) => ({
        pnr: ticket.pnr,
        boardingPoint: ticket.boardingPoint,
        dropOffPoint: ticket.dropOffPoint,
        phoneNumber: ticket.phoneNumber,
        email: ticket.email,
        status: ticket.status,
        seats: ticket.seats
            .filter((seat) => seat.isActive)
            .map((seat) => ({
                seatNumber: seat.seatNumber,
                ladiesSeat: seat.ladiesSeat,
                isActive: seat.isActive,
            })),
        travellers: ticket.travellers
            .filter((traveller) => !traveller.isCancelled)
            .map((traveller) => ({
                fullName: traveller.fullName,
                gender: traveller.gender,
                seatNumber: traveller.seatNumber,
            })),
    })).filter((ticket) => ticket.seats.length > 0),
})).filter((trip) => trip.tickets.length > 0);

// Cancellation rows the server should look up by remarks PNR.
export const buildCancellationPayloadRows = (tickets) => {
    if (!isValidArray(tickets)) {
        return [];
    }
    return tickets
        .filter(isTicketEligible)
        .filter(isCancelledTicket)
        .map((ticket) => ({
            tripKey: ticket.tripKey,
            pnr: ticket.pnr,
            journeyDate: ticket.journeyDate,
            route: ticket.route,
            serviceNumber: ticket.serviceNumber,
            boardingPoint: ticket.boardingPoint,
            dropOffPoint: ticket.dropOffPoint,
        }));
};

// Build the bulk-booking payload using only the rows the operator has
// selected. Each key is `${tripKey}__${pnr}`. We forward `bookedBy` per
// ticket so the server can stitch it into the remarks, and the full
// per-seat finance breakdown so the inserted ticket_details / travellers
// rows match the file exactly.
export const buildBookingPayloadTrips = (trips, selectedKeys, seatOverrides) => {
    if (!isValidArrayWithValues(selectedKeys)) {
        return [];
    }
    const selectionByTrip = new Map();
    selectedKeys.forEach((key) => {
        const sep = key.lastIndexOf('__');
        if (sep === -1) return;
        const tripKey = key.slice(0, sep);
        const pnr = key.slice(sep + 2);
        if (!selectionByTrip.has(tripKey)) {
            selectionByTrip.set(tripKey, new Set());
        }
        selectionByTrip.get(tripKey).add(pnr);
    });
    const overridesByKey = seatOverrides || {};
    return trips
        .filter((trip) => selectionByTrip.has(trip.tripKey))
        .map((trip) => {
            const selectedPnrs = selectionByTrip.get(trip.tripKey);
            return {
                tripKey: trip.tripKey,
                journeyDate: trip.journeyDate,
                route: trip.route,
                serviceNumber: trip.serviceNumber,
                coach: trip.coach,
                tickets: trip.tickets
                    .filter((ticket) => selectedPnrs.has(ticket.pnr))
                    .map((ticket) => {
                        // Apply any operator-chosen seat overrides for this
                        // PNR before assembling the booking payload.
                        const ticketKey = `${trip.tripKey}__${ticket.pnr}`;
                        const overridesForTicket = overridesByKey[ticketKey] || {};
                        const activeSeats = ticket.seats
                            .filter((seat) => seat.isActive)
                            .map((seat) => {
                                const replacement = overridesForTicket[seat.seatNumber];
                                if (replacement) {
                                    return { ...seat, seatNumber: replacement, originalSeatNumber: seat.seatNumber };
                                }
                                return seat;
                            });
                        const sum = (key) => activeSeats.reduce((acc, s) => acc + (s[key] || 0), 0);
                        const totals = {
                            fare: sum('fare'),
                            discount: sum('discount'),
                            cgst: sum('cgst'),
                            sgst: sum('sgst'),
                            totalGst: sum('totalGst'),
                            ticketCost: sum('ticketCost'),
                            amountPaid: sum('amountPaid'),
                            commission: sum('commission'),
                        };
                        return {
                            pnr: ticket.pnr,
                            boardingPoint: ticket.boardingPoint,
                            dropOffPoint: ticket.dropOffPoint,
                            phoneNumber: ticket.phoneNumber,
                            email: ticket.email,
                            // File's own remarks column is intentionally
                            // omitted — only generated "Booked by, PNR" is
                            // persisted.
                            bookedBy: ticket.bookedBy,
                            bookedOn: ticket.bookedOn,
                            totalCost: totals.ticketCost,
                            fare: totals.fare,
                            cgst: totals.cgst,
                            sgst: totals.sgst,
                            totalGst: totals.totalGst,
                            commission: totals.commission,
                            discount: totals.discount,
                            amountPaid: totals.amountPaid,
                            seats: activeSeats.map((seat) => ({
                                seatNumber: seat.seatNumber,
                                fare: seat.fare,
                                discount: seat.discount,
                                cgst: seat.cgst,
                                sgst: seat.sgst,
                                totalGst: seat.totalGst,
                                ticketCost: seat.ticketCost,
                                amountPaid: seat.amountPaid,
                                commission: seat.commission,
                                ladiesSeat: seat.ladiesSeat,
                                gender: seat.ladiesSeat ? 'F' : 'M',
                            })),
                            travellers: ticket.travellers
                                .filter((traveller) => !traveller.isCancelled)
                                .map((traveller) => ({
                                    fullName: traveller.fullName,
                                    gender: traveller.gender,
                                    age: traveller.age || 0,
                                    seatNumber: traveller.seatNumber,
                                })),
                        };
                    }),
            };
        });
};

// Flatten validation.results into a list of {valid, invalid} tickets and
// enrich each entry with the original parsed-ticket details (name,
// boarding, drop, fare, travellers) so the operator can see what they're
// about to import without drilling into every trip. Applies seat overrides:
// a ticket whose only outstanding issue was a seat conflict that's been
// resolved gets promoted from Invalid → Valid.
export const buildValidationBreakdown = (validation, tickets, seatOverrides) => {
    const ticketLookup = new Map();
    tickets.forEach((ticket) => {
        ticketLookup.set(`${ticket.tripKey}__${ticket.pnr}`, ticket);
    });
    const overridesByKey = seatOverrides || {};
    const valid = [];
    const invalid = [];
    validation.results.forEach((groupResult) => {
        groupResult.tickets.forEach((ticketResult) => {
            const key = `${groupResult.tripKey}__${ticketResult.pnr}`;
            const original = ticketLookup.get(key) || {};
            const overrides = overridesByKey[key] || {};

            const displaySeats = (ticketResult.seats || []).map((seat) => {
                const replacement = overrides[seat.seatNumber];
                if (!replacement) return seat;
                return {
                    ...seat,
                    canImport: true,
                    status: 'reassigned',
                    reason: `Reassigned to ${replacement}`,
                    replacementSeatNumber: replacement,
                    originalSeatNumber: seat.seatNumber,
                };
            });

            const issuesAfterOverride = (ticketResult.issues || []).filter((issue) => {
                const match = issue.match(/Seat\s+(\S+)\s+/i);
                if (match && overrides[match[1]]) {
                    return false;
                }
                return true;
            });
            const stillFailingSeats = displaySeats.filter((s) => !s.canImport);
            const effectiveCanImport = ticketResult.canImport
                || (issuesAfterOverride.length === 0 && stillFailingSeats.length === 0
                    && groupResult.tripFound
                    && Object.keys(overrides).length > 0);

            const flattened = {
                key,
                tripKey: groupResult.tripKey,
                journeyDate: groupResult.journeyDate,
                route: groupResult.route,
                serviceNumber: groupResult.serviceNumber,
                pnr: ticketResult.pnr,
                boardingPoint: original.boardingPoint || '',
                dropOffPoint: original.dropOffPoint || '',
                phoneNumber: original.phoneNumber || '',
                travellerNames: isValidArray(original.travellers)
                    ? original.travellers
                        .filter((t) => !t.isCancelled)
                        .map((t) => t.fullName || '(no name)')
                    : [],
                seats: displaySeats,
                totalFare: original.totals ? original.totals.fare : 0,
                issues: issuesAfterOverride,
                hasOverrides: Object.keys(overrides).length > 0,
                tripFound: groupResult.tripFound,
                tripIssue: groupResult.tripIssue,
                matchedTrip: groupResult.trip || null,
                resolvedTripRoute: ticketResult.resolvedTripRoute || null,
            };
            if (effectiveCanImport) {
                valid.push(flattened);
            } else {
                invalid.push(flattened);
            }
        });
    });
    return { valid, invalid };
};
